import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { X, Sparkles, Image as ImageIcon, Video, Upload, Loader2, Download, Trash2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* Design & Render Center — admin-only AI media generation hub.
   Four modes (text→img · text→vid · img→img · img→vid). Video jobs poll
   /admin/render/jobs/{id} every 4s until completed. Result feed shows
   thumbnails / inline video for the user's recent jobs. */

const MODES = [
  { id: 'text-to-image',  label: 'Text → Image', kind: 'image', icon: ImageIcon, accent: '#3B82F6', endpoint: 'text-to-image' },
  { id: 'image-to-image', label: 'Image + Text → Image', kind: 'image', icon: Wand2,   accent: '#8B5CF6', endpoint: 'image-to-image' },
  { id: 'text-to-video',  label: 'Text → Video', kind: 'video', icon: Video,    accent: '#EC4899', endpoint: 'text-to-video' },
  { id: 'image-to-video', label: 'Image + Text → Video', kind: 'video', icon: Video,   accent: '#F59E0B', endpoint: 'image-to-video' },
  // iter-79: Smart-Frame video → video. Backend extracts the first
  // frame of the uploaded clip via ffmpeg and routes it through the
  // image-to-video pathway. Source motion is not preserved — composition
  // is. UX-wise this lives alongside the other modes so admins can
  // choose between "animate this still" and "re-imagine this clip".
  { id: 'video-to-video', label: 'Video + Text → Video', kind: 'video', icon: Video,   accent: '#06B6D4', endpoint: 'video-to-video' },
];

export default function RenderCenterDialog({ open, onClose, hasFullAccess, adminToken, areas = [] }) {
  const [mode, setMode] = useState('text-to-image');
  const [prompt, setPrompt] = useState('');
  const [sourceImage, setSourceImage] = useState(null); // { dataUrl, base64 }
  const [sourceVideo, setSourceVideo] = useState(null); // { dataUrl, base64, name, size }
  const [duration, setDuration] = useState(8);
  const [aspect, setAspect] = useState('16:9');
  const [submitting, setSubmitting] = useState(false);
  const [jobs, setJobs] = useState([]);
  const fileInputRef = useRef(null);

  const auth = adminToken ? { headers: { Authorization: `Bearer ${adminToken}` } } : {};
  const currentMode = MODES.find((m) => m.id === mode);
  const requiresImage = mode === 'image-to-image' || mode === 'image-to-video';
  const requiresVideo = mode === 'video-to-video';
  const isVideoMode = currentMode?.kind === 'video';

  const refreshJobs = useCallback(async () => {
    if (!hasFullAccess) return;
    try {
      const { data } = await axios.get(`${API}/admin/render/jobs`, auth);
      setJobs(data.jobs || []);
    } catch (_) { /* noop */ }
  }, [hasFullAccess, adminToken]);

  // Refresh on open + poll every 5s while any job is pending/running.
  useEffect(() => {
    if (!open) return;
    refreshJobs();
    const id = setInterval(() => {
      // Only poll if we have at least one in-flight job.
      const hasInFlight = (jobs || []).some((j) => j.status === 'pending' || j.status === 'running');
      if (hasInFlight) refreshJobs();
    }, 5000);
    return () => clearInterval(id);
  }, [open, refreshJobs, jobs]);

  const onFile = (file) => {
    if (!file) return;
    if (requiresVideo) {
      if (!file.type.startsWith('video/')) { toast.error('Please pick a video file (MP4 / MOV / WebM).'); return; }
      if (file.size > 25 * 1024 * 1024) { toast.error('Video too large (max 25MB).'); return; }
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result || '';
        const base64 = String(dataUrl).split(',')[1] || '';
        setSourceVideo({ dataUrl, base64, name: file.name, size: file.size });
      };
      reader.readAsDataURL(file);
      return;
    }
    if (!file.type.startsWith('image/')) { toast.error('Please pick an image file.'); return; }
    if (file.size > 6 * 1024 * 1024) { toast.error('Image too large (max 6MB).'); return; }
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result || '';
      const base64 = String(dataUrl).split(',')[1] || '';
      setSourceImage({ dataUrl, base64 });
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!hasFullAccess) return;
    if (!prompt.trim()) { toast.error('Type a prompt first.'); return; }
    if (requiresImage && !sourceImage?.base64) { toast.error('Pick a source image for this mode.'); return; }
    if (requiresVideo && !sourceVideo?.base64) { toast.error('Pick a source video for this mode.'); return; }
    setSubmitting(true);
    try {
      const body = { prompt: prompt.trim() };
      if (requiresImage) body.image_base64 = sourceImage.base64;
      if (requiresVideo) body.video_base64 = sourceVideo.base64;
      if (isVideoMode) {
        body.duration = duration;
        body.aspect_ratio = aspect;
      }
      const { data } = await axios.post(`${API}/admin/render/${currentMode.endpoint}`, body, { ...auth, timeout: 180000 });
      if (data.status === 'completed') {
        toast.success('Image ready');
      } else {
        toast.success('Video job queued — refreshing every 5 seconds…');
      }
      // Reset prompt but keep source image so user can iterate.
      setPrompt('');
      refreshJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const removeJob = async (jobId) => {
    if (!window.confirm('Remove this from your render history?')) return;
    try {
      await axios.delete(`${API}/admin/render/jobs/${jobId}`, auth);
      setJobs((js) => js.filter((j) => j.id !== jobId));
    } catch (e) { toast.error('Could not delete'); }
  };

  if (!open) return null;

  if (!hasFullAccess) {
    return (
      <div data-testid="render-center-dialog" className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(8px)' }}>
        <div className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl" style={{ backgroundColor: '#FAF8F2' }}>
          <div className="px-6 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)' }}>
            <p className="text-base font-bold text-white" style={{ fontFamily: 'Manrope' }}>Design & Render Center</p>
            <button onClick={onClose} type="button" aria-label="Close" className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center hover:scale-110 active:scale-90 transition-transform duration-75 cursor-pointer" style={{ touchAction: 'manipulation' }}><X className="w-5 h-5" strokeWidth={2.5} /></button>
          </div>
          <div className="p-6 text-center">
            <Sparkles className="w-10 h-10 mx-auto mb-3" style={{ color: '#6366F1' }} />
            <p className="text-sm" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
              The Design & Render Center is reserved for admin and engineer roles. Sign in with the right credentials to generate concept renders and walkthrough videos.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="render-center-dialog" className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-6xl max-h-[92vh] flex flex-col rounded-2xl overflow-hidden shadow-2xl" style={{ backgroundColor: '#FAF8F2' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/80" style={{ fontFamily: 'Manrope' }}>AI Generation</p>
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope' }}>Design & Render Center</h2>
            </div>
          </div>
          <button data-testid="render-center-close" onClick={onClose} type="button" aria-label="Close" className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center hover:scale-110 active:scale-90 transition-transform duration-75 cursor-pointer" style={{ touchAction: 'manipulation' }}>
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>

        {/* Mode tabs */}
        <div className="flex flex-wrap gap-2 px-6 py-3 border-b" style={{ borderColor: '#E2DBC8', backgroundColor: '#F5F0E1' }}>
          {MODES.map((m) => (
            <button
              key={m.id}
              data-testid={`render-mode-${m.id}`}
              onClick={() => setMode(m.id)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-bold transition"
              style={{
                backgroundColor: mode === m.id ? m.accent : 'white',
                color: mode === m.id ? 'white' : '#1A2E2E',
                border: `1px solid ${mode === m.id ? m.accent : '#D6D0C0'}`,
                fontFamily: 'Manrope',
              }}
            >
              <m.icon className="w-3.5 h-3.5" /> {m.label}
            </button>
          ))}
        </div>

        {/* Body — composer left, history right */}
        <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-5 gap-0">
          {/* Composer */}
          <div className="lg:col-span-2 p-6 overflow-y-auto border-r" style={{ borderColor: '#E2DBC8', backgroundColor: '#FAF8F2' }}>
            {requiresImage && (
              <div className="mb-4">
                <label className="block text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>
                  Source image
                </label>
                {sourceImage ? (
                  <div className="relative rounded-xl overflow-hidden" style={{ border: '1px solid #D6D0C0' }}>
                    <img src={sourceImage.dataUrl} alt="Source" className="w-full max-h-48 object-cover" />
                    <button
                      data-testid="render-clear-source"
                      onClick={() => setSourceImage(null)}
                      className="absolute top-2 right-2 p-1.5 rounded-full text-white"
                      style={{ backgroundColor: 'rgba(15,23,42,0.7)' }}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    data-testid="render-pick-source"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full py-8 rounded-xl border-2 border-dashed flex flex-col items-center gap-2 transition hover:bg-amber-50/40"
                    style={{ borderColor: '#D6D0C0', color: '#5C6B6B', fontFamily: 'Manrope' }}
                  >
                    <Upload className="w-6 h-6" />
                    <span className="text-xs font-bold">Pick a starting image</span>
                    <span className="text-[10px]">(JPG / PNG · max 6MB)</span>
                  </button>
                )}
                <input
                  data-testid="render-file-input"
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onFile(e.target.files?.[0])}
                />
              </div>
            )}

            {requiresVideo && (
              <div className="mb-4">
                <label className="block text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>
                  Source video
                </label>
                {sourceVideo ? (
                  <div className="relative rounded-xl overflow-hidden" style={{ border: '1px solid #D6D0C0' }}>
                    <video
                      src={sourceVideo.dataUrl}
                      className="w-full max-h-48 object-cover bg-black"
                      controls
                      muted
                      playsInline
                    />
                    <button
                      data-testid="render-clear-source-video"
                      onClick={() => setSourceVideo(null)}
                      className="absolute top-2 right-2 p-1.5 rounded-full text-white"
                      style={{ backgroundColor: 'rgba(15,23,42,0.7)' }}
                      aria-label="Remove source video"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                    <div className="px-3 py-2 text-[10px]" style={{ backgroundColor: '#F5F0E1', color: '#5C6B6B', fontFamily: 'Manrope' }}>
                      <strong>{sourceVideo.name}</strong> · {(sourceVideo.size / (1024 * 1024)).toFixed(1)} MB
                      <span className="block mt-0.5 italic">We extract the first frame and re-imagine the clip from your prompt.</span>
                    </div>
                  </div>
                ) : (
                  <button
                    data-testid="render-pick-source-video"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full py-8 rounded-xl border-2 border-dashed flex flex-col items-center gap-2 transition hover:bg-amber-50/40"
                    style={{ borderColor: '#D6D0C0', color: '#5C6B6B', fontFamily: 'Manrope' }}
                  >
                    <Upload className="w-6 h-6" />
                    <span className="text-xs font-bold">Pick a starting clip</span>
                    <span className="text-[10px]">(MP4 / MOV / WebM · max 25MB)</span>
                  </button>
                )}
                {/* The shared fileInputRef is rendered above by the
                    requiresImage branch. When the mode is video-to-
                    video we mount our own input here so the `accept`
                    attribute is correct. */}
                <input
                  data-testid="render-file-input-video"
                  ref={fileInputRef}
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm"
                  className="hidden"
                  onChange={(e) => onFile(e.target.files?.[0])}
                />
              </div>
            )}

            <label className="block text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>
              Prompt
            </label>
            <textarea
              data-testid="render-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={isVideoMode ? 4 : 6}
              placeholder={
                mode === 'text-to-image'  ? 'A photo-real concept render of an oceanfront infinity pool at golden hour, Caribbean tropical landscaping, modern lounge furniture' :
                mode === 'image-to-image' ? 'Make the lighting golden hour and add scattered Adirondack chairs along the edge' :
                mode === 'text-to-video'  ? 'A slow cinematic dolly-in along a freshly renovated Caribbean lobby — terrazzo floors, palm columns, late-afternoon sunlight pouring through louvered windows' :
                mode === 'video-to-video' ? 'Re-imagine this clip as a golden-hour cinematic, warmer tones, gentle steady-cam dolly motion, lush tropical planting in the foreground' :
                                            'Animate this photo into a cinematic 8-second walk-through with a slow dolly-forward camera move'
              }
              className="w-full px-4 py-3 rounded-xl text-sm border outline-none focus:ring-2 resize-none"
              style={{ borderColor: '#D6D0C0', fontFamily: 'Manrope' }}
            />

            {isVideoMode && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>Duration (seconds)</label>
                  <select data-testid="render-duration" value={duration} onChange={(e) => setDuration(parseInt(e.target.value))} className="w-full px-3 py-2 rounded-lg text-sm border" style={{ borderColor: '#D6D0C0', fontFamily: 'Manrope' }}>
                    <option value={4}>4s</option>
                    <option value={8}>8s</option>
                    <option value={12}>12s</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>Aspect ratio</label>
                  <select data-testid="render-aspect" value={aspect} onChange={(e) => setAspect(e.target.value)} className="w-full px-3 py-2 rounded-lg text-sm border" style={{ borderColor: '#D6D0C0', fontFamily: 'Manrope' }}>
                    <option value="16:9">16:9 (landscape)</option>
                    <option value="9:16">9:16 (portrait)</option>
                    <option value="1:1">1:1 (square)</option>
                  </select>
                </div>
              </div>
            )}

            <button
              data-testid="render-generate"
              disabled={submitting || !prompt.trim() || (requiresImage && !sourceImage) || (requiresVideo && !sourceVideo)}
              onClick={submit}
              className="w-full mt-4 py-3 rounded-xl text-sm font-bold text-white inline-flex items-center justify-center gap-2 transition hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
              style={{ background: `linear-gradient(135deg, ${currentMode?.accent} 0%, #8B5CF6 100%)`, fontFamily: 'Manrope' }}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {submitting ? 'Working…' : (isVideoMode ? `Generate video (~2-5 min)` : 'Generate image')}
            </button>

            <p className="mt-3 text-[10px] leading-relaxed" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
              Images render via <strong>Gemini Nano Banana</strong>. Videos render via <strong>Sora 2</strong> — they take 2-5 minutes; the job will appear in the History panel and update automatically when ready.
            </p>
          </div>

          {/* History feed */}
          <div className="lg:col-span-3 p-6 overflow-y-auto" style={{ backgroundColor: 'white' }}>
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold mb-3" style={{ color: '#5C6B6B', fontFamily: 'Manrope' }}>
              Your renders ({jobs.length})
            </p>
            {jobs.length === 0 && (
              <div className="text-center py-12 text-sm" style={{ color: '#5C6B6B', fontFamily: 'Manrope' }}>
                Nothing rendered yet. Pick a mode, type a prompt, and hit Generate.
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {jobs.map((j) => (
                <RenderJobCard key={j.id} job={j} areas={areas} adminToken={adminToken} onRemove={() => removeJob(j.id)} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RenderJobCard({ job, onRemove, areas, adminToken }) {
  const url = job.result_url ? (job.result_url.startsWith('http') ? job.result_url : `${BACKEND}${job.result_url}`) : null;
  const inFlight = job.status === 'pending' || job.status === 'running';
  const failed = job.status === 'failed';
  const [sendTo, setSendTo] = useState('');
  const [sending, setSending] = useState(false);
  const sentTo = (job.sent_to_areas || []).map((s) => s.area_id);

  const sendToArea = async () => {
    if (!sendTo) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/render/jobs/${job.id}/send-to-area`,
        { area_id: sendTo, target: 'auto' },
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      const areaName = (areas || []).find((a) => a.id === sendTo)?.name || 'area';
      toast.success(`Sent to ${areaName} → renders`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#E2DBC8', backgroundColor: 'white' }}>
      <div className="aspect-video bg-slate-100 flex items-center justify-center relative">
        {inFlight && (
          <div className="text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-1" style={{ color: '#6366F1' }} />
            <p className="text-[10px] font-bold" style={{ color: '#6366F1', fontFamily: 'Manrope' }}>{job.status === 'pending' ? 'Queued' : 'Generating…'}</p>
          </div>
        )}
        {failed && (
          <div className="text-center px-3">
            <p className="text-[10px] font-bold" style={{ color: '#DC2626', fontFamily: 'Manrope' }}>Failed</p>
            <p className="text-[9px] mt-1" style={{ color: '#5C6B6B' }}>{(job.error || '').slice(0, 90)}</p>
          </div>
        )}
        {!inFlight && !failed && url && job.result_kind === 'image' && (
          <img src={url} alt={job.params?.prompt || 'render'} className="w-full h-full object-cover" />
        )}
        {!inFlight && !failed && url && job.result_kind === 'video' && (
          <video src={url} controls className="w-full h-full object-cover" />
        )}
      </div>
      {/* iter-79: warning surface — e.g. "source frame was skipped"
          when the Sora 2 SDK's image conditioning is upstream-broken.
          The clip still renders from the prompt, but the admin needs
          to know the source was ignored. */}
      {job.warn && (
        <div className="px-3 py-1.5 text-[10px] leading-snug" style={{ backgroundColor: '#FFF8E1', borderTop: '1px solid #FDE68A', color: '#92400E', fontFamily: 'Manrope' }}>
          <strong>Heads up:</strong> {job.warn}
        </div>
      )}
      <div className="px-3 py-2">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <span className="text-[8px] uppercase tracking-wider font-bold" style={{ color: '#6366F1', fontFamily: 'Manrope' }}>
            {job.mode}
          </span>
          <div className="flex items-center gap-1">
            {url && (
              <a href={url} download className="p-1 rounded hover:bg-slate-100" title="Download">
                <Download className="w-3 h-3" style={{ color: '#1F4FBE' }} />
              </a>
            )}
            <button onClick={onRemove} className="p-1 rounded hover:bg-red-50" title="Remove from history">
              <Trash2 className="w-3 h-3" style={{ color: '#DC2626' }} />
            </button>
          </div>
        </div>
        <p className="text-[10px] line-clamp-2 mb-1.5" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>
          {job.params?.prompt || ''}
        </p>
        {/* Send-to-area picker — appears once the render is finished. */}
        {!inFlight && !failed && url && (
          <div className="flex items-center gap-1 pt-1.5 border-t" style={{ borderColor: '#F1F5F9' }}>
            <select
              data-testid={`render-send-area-${job.id}`}
              value={sendTo}
              onChange={(e) => setSendTo(e.target.value)}
              className="flex-1 text-[10px] px-2 py-1 rounded border outline-none"
              style={{ borderColor: '#D6D0C0', fontFamily: 'Manrope', color: '#1A2E2E' }}
            >
              <option value="">Send to area…</option>
              {(areas || []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}{sentTo.includes(a.id) ? ' ✓' : ''}
                </option>
              ))}
            </select>
            <button
              data-testid={`render-send-go-${job.id}`}
              disabled={!sendTo || sending}
              onClick={sendToArea}
              className="px-2.5 py-1 rounded text-[10px] font-bold text-white inline-flex items-center gap-1 disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)', fontFamily: 'Manrope' }}
              title="Attach this render to the selected area"
            >
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : '→ Send'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
